@if (isLoading()) {
<app-loader></app-loader>
}

<div class="container mx-auto py-6 px-4" [class.d-none]="isLoading()">
  <div class="cv-content page-wrapper">
    <!-- Controls Section -->
    <div *ngIf="!publicUserId" class="card shadow-sm mb-6 d-print-none">
      <div class="card-body">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <h2 class="h4 mb-0">{{'My Resume' | translate}}</h2>
          <div class="flex flex-wrap items-center gap-2">
            <!-- Edit mode buttons -->
            <ng-container *ngIf="isEditMode()">
              <button class="btn btn-success" (click)="saveCvSettings()">
                <fa-icon [icon]="faSave" class="mr-2"></fa-icon>
                {{'Save' | translate}}
              </button>
              <button class="btn btn-secondary" (click)="cancelEditMode()">
                <fa-icon [icon]="faTimes" class="mr-2"></fa-icon>
                {{'Cancel' | translate}}
              </button>
            </ng-container>
            <!-- Normal mode buttons -->
            <ng-container *ngIf="!isEditMode()">
              <button class="btn btn-outline-primary" (click)="toggleEditMode()">
                <fa-icon [icon]="faPencilAlt" class="mr-2"></fa-icon>
                {{'Edit CV' | translate}}
              </button>
              <button class="btn btn-outline-warning" *ngIf="hasEdits()" (click)="regenerateCv()">
                <fa-icon [icon]="faSync" class="mr-2"></fa-icon>
                {{'Regenerate CV' | translate}}
              </button>
              <small class="text-gray-500 mr-2">
                {{(isPublic() ? 'Your CV is now visible to everyone' : 'Your CV is private') | translate}}
              </small>
              <button class="btn" [ngClass]="isPublic() ? 'btn-danger' : 'btn-success'" (click)="togglePublicCV()">
                <fa-icon [icon]="isPublic() ? faEyeSlash : faEye" class="mr-2"></fa-icon>
                {{(isPublic() ? 'Make Private' : 'Make Public') | translate}}
              </button>
              <button class="btn btn-primary" *ngIf="isPublic()" (click)="copyPublicLink()">
                <fa-icon [icon]="faCopy" class="mr-2"></fa-icon>
                {{'Copy Link' | translate}}
              </button>
            </ng-container>
          </div>
        </div>
        <!-- Edit mode hint -->
        <div *ngIf="isEditMode()" class="alert alert-info mt-4 mb-0">
          <small>{{'Click the eye icon next to each section to show or hide it on your CV. Hidden items appear faded.' | translate}}</small>
        </div>
      </div>
    </div>

    <!-- Main CV Content -->
    <div class="card shadow-sm d-print-block">
      <!-- Header Section -->
      <div class="bg-mty-primary text-white p-6 md:px-10 md:py-8">
        <div class="flex flex-col md:flex-row items-center md:items-start">
          <!-- Profile Image -->
          <div class="cv-profile-image rounded-full border-2 border-white overflow-hidden">
            <img class="profile-image w-full h-full object-cover" [src]="entity()?.avatarUri ? entity()!.avatarUri : avatarPlaceholder">
          </div>

          <!-- Personal Information -->
          <div class="mt-4 md:mt-0 md:ml-10 text-center md:text-left">
            <h1 class="h2 mb-2 text-white">{{entity()?.firstName}} {{entity()?.lastName}}</h1>
            <h2 class="h4 font-normal mb-6 text-white">{{ProfileTypeUtils.getName(entity()?.profileType) |
              translate}}</h2>
            <p class="mb-1 flex items-center justify-center md:justify-start" *ngIf="entity()?.birthDate">
              <fa-icon [icon]="faBirthdayCake" [fixedWidth]="true" class="mr-2"></fa-icon>
              {{entity()?.birthDate | date}}
            </p>
            <p class="mb-1 flex items-center justify-center md:justify-start" *ngIf="entity()?.homeAddress">
              <fa-icon [icon]="faMapMarker" [fixedWidth]="true" class="mr-2"></fa-icon>
              {{entity()?.homeAddress?.street}} {{entity()?.homeAddress?.number}}, {{entity()?.homeAddress?.city}},
              {{locale.getCountryName(entity()?.homeAddress?.country)}}
            </p>
            <p class="mb-1 flex items-center justify-center md:justify-start" *ngIf="entity()?.phoneNumber">
              <fa-icon [icon]="faPhone" [fixedWidth]="true" class="mr-2"></fa-icon>
              {{entity()?.phoneNumber}}
            </p>
            <p class="mb-1 flex items-center justify-center md:justify-start" *ngIf="entity()?.emailAddress">
              <fa-icon [icon]="faEnvelope" [fixedWidth]="true" class="mr-2"></fa-icon>
              {{entity()?.emailAddress}}
            </p>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="grid grid-cols-1 md:grid-cols-12 gap-6 p-6 md:p-8">
        <!-- Left Column -->
        <div class="md:col-span-4">
          <!-- Professional Skills -->
          <div class="mb-10" *ngIf="entity()?.areaOfInterests?.length && (isEditMode() || !isSectionHidden(CV_SECTIONS.PROFESSIONAL_SKILLS))" [class.cv-section-hidden]="isSectionHidden(CV_SECTIONS.PROFESSIONAL_SKILLS)">
            <h3 class="cv-section-heading flex items-center justify-between">
              <span>{{'Professional Skills' | translate}}</span>
              <fa-icon *ngIf="isEditMode()" class="cv-edit-toggle" [icon]="isSectionHidden(CV_SECTIONS.PROFESSIONAL_SKILLS) ? faEyeSlash : faEye" (click)="toggleSectionVisibility(CV_SECTIONS.PROFESSIONAL_SKILLS)"></fa-icon>
            </h3>
            <ul class="space-y-2">
              <li class="flex items-center" *ngFor="let interest of entity()?.areaOfInterests; trackBy: trackById">
                <fa-icon [icon]="faCheck" class="text-mty-primary mr-2"></fa-icon>
                <span>{{interestService.getTranslatedName(interest)}}</span>
              </li>
            </ul>
          </div>

          <!-- Languages with 6 levels -->
          <div class="mb-10" *ngIf="entity()?.languageProficiencies?.length && (isEditMode() || !isSectionHidden(CV_SECTIONS.LANGUAGES))" [class.cv-section-hidden]="isSectionHidden(CV_SECTIONS.LANGUAGES)">
            <h3 class="cv-section-heading flex items-center justify-between">
              <span>{{'Languages' | translate}}</span>
              <fa-icon *ngIf="isEditMode()" class="cv-edit-toggle" [icon]="isSectionHidden(CV_SECTIONS.LANGUAGES) ? faEyeSlash : faEye" (click)="toggleSectionVisibility(CV_SECTIONS.LANGUAGES)"></fa-icon>
            </h3>
            <div class="space-y-3">
              <div *ngFor="let lang of entity()?.languageProficiencies; trackBy: trackById" class="cv-language-card">
                <h4 class="text-sm font-semibold mb-3">{{locale.getLanguageName(lang.languageCode)}}</h4>
                <div class="flex items-center justify-between mb-2">
                  <small class="text-gray-500">{{'Speaking' | translate}}</small>
                  <div class="flex gap-1.5">
                    <span *ngFor="let filled of getCircleArray(lang.speakingProficiency); trackBy: trackByIndex" class="cv-level-dot" [class.cv-level-dot--on]="filled"></span>
                  </div>
                </div>
                <div class="flex items-center justify-between mb-2">
                  <small class="text-gray-500">{{'Reading' | translate}}</small>
                  <div class="flex gap-1.5">
                    <span *ngFor="let filled of getCircleArray(lang.readingProficiency); trackBy: trackByIndex" class="cv-level-dot" [class.cv-level-dot--on]="filled"></span>
                  </div>
                </div>
                <div class="flex items-center justify-between">
                  <small class="text-gray-500">{{'Writing' | translate}}</small>
                  <div class="flex gap-1.5">
                    <span *ngFor="let filled of getCircleArray(lang.writingProficiency); trackBy: trackByIndex" class="cv-level-dot" [class.cv-level-dot--on]="filled"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Hobbies/Interests -->
          <div class="mb-10" *ngIf="entity()?.interests?.length && (isEditMode() || !isSectionHidden(CV_SECTIONS.HOBBIES))" [class.cv-section-hidden]="isSectionHidden(CV_SECTIONS.HOBBIES)">
            <h3 class="cv-section-heading flex items-center justify-between">
              <span>{{'Hobbies & Interests' | translate}}</span>
              <fa-icon *ngIf="isEditMode()" class="cv-edit-toggle" [icon]="isSectionHidden(CV_SECTIONS.HOBBIES) ? faEyeSlash : faEye" (click)="toggleSectionVisibility(CV_SECTIONS.HOBBIES)"></fa-icon>
            </h3>
            <ul class="space-y-2">
              <li class="flex items-center" *ngFor="let hobby of entity()?.interests; trackBy: trackById">
                <fa-icon [icon]="faCheck" class="text-mty-primary mr-2"></fa-icon>
                <span>{{hobby.description}}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Right Column -->
        <div class="md:col-span-8">
          <!-- About Me -->
          <div class="mb-10" *ngIf="hasAboutMe() && (isEditMode() || !isSectionHidden(CV_SECTIONS.ABOUT_ME))" [class.cv-section-hidden]="isSectionHidden(CV_SECTIONS.ABOUT_ME)">
            <h3 class="cv-section-heading flex items-center justify-between">
              <span>{{'About me' | translate}}</span>
              <fa-icon *ngIf="isEditMode()" class="cv-edit-toggle" [icon]="isSectionHidden(CV_SECTIONS.ABOUT_ME) ? faEyeSlash : faEye" (click)="toggleSectionVisibility(CV_SECTIONS.ABOUT_ME)"></fa-icon>
            </h3>
            <!-- Edit mode: textarea -->
            <textarea *ngIf="isEditMode() && !isSectionHidden(CV_SECTIONS.ABOUT_ME)" class="form-control mb-2" rows="6" [ngModel]="aboutMeText()" (ngModelChange)="updateAboutMe($event)" placeholder="{{'Enter your personal summary...' | translate}}"></textarea>
            <!-- Restore AI Summary button - only visible when user has edited the summary -->
            <button *ngIf="isEditMode() && !isSectionHidden(CV_SECTIONS.ABOUT_ME) && cvSettings().editedAboutMe && entity()?.aiGeneratedProfileSummary" class="btn btn-sm btn-outline-secondary mt-2" (click)="restoreAiSummary()">
              <fa-icon [icon]="faSync" class="mr-2"></fa-icon>
              {{'Restore AI Summary' | translate}}
            </button>
            <!-- View mode: markdown display -->
            <markdown *ngIf="!isEditMode()">{{aboutMeText()}}</markdown>
          </div>

          <!-- Education -->
          <div class="mb-10" *ngIf="entity()?.educations?.length && (isEditMode() || !isSectionHidden(CV_SECTIONS.EDUCATION))" [class.cv-section-hidden]="isSectionHidden(CV_SECTIONS.EDUCATION)">
            <h3 class="cv-section-heading flex items-center justify-between">
              <span>{{'Education' | translate}}</span>
              <fa-icon *ngIf="isEditMode()" class="cv-edit-toggle" [icon]="isSectionHidden(CV_SECTIONS.EDUCATION) ? faEyeSlash : faEye" (click)="toggleSectionVisibility(CV_SECTIONS.EDUCATION)"></fa-icon>
            </h3>
            <div class="timeline">
              <ng-container *ngFor="let edu of entity()?.educations; trackBy: trackById">
                <div class="mb-6" *ngIf="isEditMode() || !isItemHidden('education', edu.id)" [class.cv-item-hidden]="isItemHidden('education', edu.id)">
                  <div class="card border-0 shadow-sm">
                    <div class="card-body">
                      <div class="flex items-start justify-between">
                        <h4 class="text-base font-semibold text-mty-primary mb-1">{{edu.programName}}</h4>
                        <fa-icon *ngIf="isEditMode()" class="cv-edit-toggle ml-2" [icon]="isItemHidden('education', edu.id) ? faEyeSlash : faEye" (click)="toggleItemVisibility('education', edu.id)"></fa-icon>
                      </div>
                      <h5 class="text-sm text-gray-500 mb-2">
                        {{edu.schoolName}} - {{edu.schoolCity}}, {{locale.getCountryName(edu.schoolCountry)}}
                        <span class="ml-2">({{edu.level}})</span>
                      </h5>
                      <div class="text-sm text-gray-500 mb-2 flex items-center">
                        <fa-icon [icon]="faCalendar" class="mr-2"></fa-icon>
                        {{edu.startDate | date}} - {{edu.isCurrent ? ('Present' | translate) : (edu.endDate | date)}}
                      </div>
                      <p class="mb-2">{{edu.description}}</p>
                      <!-- Extra activities/courses -->
                      <div *ngIf="edu.extras" class="mt-2">
                        <small class="text-gray-500 font-bold">{{ "Courses / minors / electives" | translate
                          }}</small>
                        <p class="text-sm mb-0 ml-3">{{edu.extras}}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </ng-container>
            </div>
          </div>

          <!-- Work Experience -->
          <div class="mb-10" *ngIf="entity()?.workExperiences?.length && (isEditMode() || !isSectionHidden(CV_SECTIONS.WORK_EXPERIENCE))" [class.cv-section-hidden]="isSectionHidden(CV_SECTIONS.WORK_EXPERIENCE)">
            <h3 class="cv-section-heading flex items-center justify-between">
              <span>{{'Work Experience' | translate}}</span>
              <fa-icon *ngIf="isEditMode()" class="cv-edit-toggle" [icon]="isSectionHidden(CV_SECTIONS.WORK_EXPERIENCE) ? faEyeSlash : faEye" (click)="toggleSectionVisibility(CV_SECTIONS.WORK_EXPERIENCE)"></fa-icon>
            </h3>
            <div class="timeline">
              <ng-container *ngFor="let work of entity()?.workExperiences; trackBy: trackById">
                <div class="mb-6" *ngIf="isEditMode() || !isItemHidden('workExperience', work.id)" [class.cv-item-hidden]="isItemHidden('workExperience', work.id)">
                  <div class="card border-0 shadow-sm">
                    <div class="card-body">
                      <div class="flex items-start justify-between">
                        <h4 class="text-base font-semibold text-mty-primary mb-1">{{work.jobTitle}}</h4>
                        <fa-icon *ngIf="isEditMode()" class="cv-edit-toggle ml-2" [icon]="isItemHidden('workExperience', work.id) ? faEyeSlash : faEye" (click)="toggleItemVisibility('workExperience', work.id)"></fa-icon>
                      </div>
                      <h5 class="text-sm text-gray-500 mb-2">
                        {{work.companyName}} - {{work.location}}
                      </h5>
                      <div class="text-sm text-gray-500 mb-2 flex items-center">
                        <fa-icon [icon]="faCalendar" class="mr-2"></fa-icon>
                        {{work.startDate | date}} - {{work.isCurrent ? ('Present' | translate) : (work.endDate | date)}}
                      </div>
                      <p class="mb-0">{{work.description}}</p>
                    </div>
                  </div>
                </div>
              </ng-container>
            </div>
          </div>

          <!-- Foreign Experiences -->
          <div class="mb-10" *ngIf="entity()?.foreignExperiences?.length && (isEditMode() || !isSectionHidden(CV_SECTIONS.FOREIGN_EXPERIENCE))" [class.cv-section-hidden]="isSectionHidden(CV_SECTIONS.FOREIGN_EXPERIENCE)">
            <h3 class="cv-section-heading flex items-center justify-between">
              <span>{{'Foreign experience' | translate}}</span>
              <fa-icon *ngIf="isEditMode()" class="cv-edit-toggle" [icon]="isSectionHidden(CV_SECTIONS.FOREIGN_EXPERIENCE) ? faEyeSlash : faEye" (click)="toggleSectionVisibility(CV_SECTIONS.FOREIGN_EXPERIENCE)"></fa-icon>
            </h3>
            <div class="timeline">
              <ng-container *ngFor="let exp of entity()?.foreignExperiences; trackBy: trackById">
                <div class="mb-6" *ngIf="isEditMode() || !isItemHidden('foreignExperience', exp.id)" [class.cv-item-hidden]="isItemHidden('foreignExperience', exp.id)">
                  <div class="card border-0 shadow-sm">
                    <div class="card-body">
                      <div class="flex items-start justify-between">
                        <h4 class="text-base font-semibold text-mty-primary mb-1">{{exp.title}}</h4>
                        <fa-icon *ngIf="isEditMode()" class="cv-edit-toggle ml-2" [icon]="isItemHidden('foreignExperience', exp.id) ? faEyeSlash : faEye" (click)="toggleItemVisibility('foreignExperience', exp.id)"></fa-icon>
                      </div>
                      <h5 class="text-sm text-gray-500 mb-2">{{locale.getCountryName(exp.country)}}</h5>
                      <div class="text-sm text-gray-500 mb-2 flex items-center">
                        <fa-icon [icon]="faCalendar" class="mr-2"></fa-icon>
                        {{exp.startDate | date}} - {{exp.endDate | date}}
                      </div>
                      <p class="mb-2">{{exp.description}}</p>
                      <p class="mb-0 text-sm text-gray-500">{{exp.companyInformation}}</p>
                    </div>
                  </div>
                </div>
              </ng-container>
            </div>

          </div>

        </div>

        <div class="md:col-span-12 mt-4 mb-10" *ngIf="dna().currentDna.dnaData && (isEditMode() || !isSectionHidden(CV_SECTIONS.DNA))" [class.cv-section-hidden]="isSectionHidden(CV_SECTIONS.DNA)">
          <div class="flex items-center justify-between mb-3" *ngIf="isEditMode()">
            <h3 class="text-lg font-semibold text-mty-primary mb-0">{{'DNA' | translate}}</h3>
            <fa-icon class="cv-edit-toggle" [icon]="isSectionHidden(CV_SECTIONS.DNA) ? faEyeSlash : faEye" (click)="toggleSectionVisibility(CV_SECTIONS.DNA)"></fa-icon>
          </div>
          <app-dna-canvas id="resume-dna-section" [data]="dna().currentDna"></app-dna-canvas>
        </div>

      </div>
      <div class="cv-footer">
        <img src="/assets/img/cv-footer.png" alt="CV Footer" class="footer-image">
      </div>
    </div>
    <div class="cv-footer-print-only">
      <img src="/assets/img/cv-footer.png" alt="CV Footer" class="footer-image">
    </div>
  </div>
</div>
